import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BuzzApiClient } from '../services/buzz-api'
import { buzzBootstrapSignature } from '../services/buzz-bootstrap-input'
import { BuzzBootstrapInput, BuzzBootstrapResponse } from '../types'

export interface BuzzCommunityProjectionState {
  session: BuzzBootstrapResponse | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

/**
 * Reads the public Equa Chat identity projection for Teams. This hook never
 * provisions a community, opens an operator gateway session, or loads messages.
 */
export function useBuzzCommunityProjection(
  input: BuzzBootstrapInput | null,
  suppliedClient?: BuzzApiClient,
): BuzzCommunityProjectionState {
  const client = useMemo(() => suppliedClient || new BuzzApiClient(), [suppliedClient])
  const contextKey = buzzBootstrapSignature(input)
  const inputRef = useRef(input)
  const contextKeyRef = useRef(contextKey)
  const sequence = useRef(0)
  const mounted = useRef(true)
  inputRef.current = input
  contextKeyRef.current = contextKey

  const [session, setSession] = useState<BuzzBootstrapResponse | null>(null)
  const [stateContextKey, setStateContextKey] = useState(contextKey)
  const [loading, setLoading] = useState(Boolean(input))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const refresh = useCallback(async () => {
    const activeInput = inputRef.current
    const activeContextKey = contextKeyRef.current
    const activeSequence = ++sequence.current
    setStateContextKey(activeContextKey)
    setSession(null)
    setError(null)
    if (!activeInput) {
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const next = await client.teamProjection(activeInput)
      if (!mounted.current
          || activeSequence !== sequence.current
          || activeContextKey !== contextKeyRef.current) return
      setSession(next)
    } catch (reason: any) {
      if (!mounted.current
          || activeSequence !== sequence.current
          || activeContextKey !== contextKeyRef.current) return
      setError(reason?.message || 'Failed to load the Equa Chat team projection.')
    } finally {
      if (mounted.current
          && activeSequence === sequence.current
          && activeContextKey === contextKeyRef.current) setLoading(false)
    }
  }, [client])

  useEffect(() => { void refresh() }, [contextKey, refresh])

  const contextIsCurrent = stateContextKey === contextKey
  return {
    session: contextIsCurrent ? session : null,
    loading: contextIsCurrent ? loading : Boolean(input),
    error: contextIsCurrent ? error : null,
    refresh,
  }
}
